import React, { useEffect, useRef, useState } from 'react';

interface CountUpProps {
    /** Full display value, e.g. "5", "2021", "24/7". */
    value: string;
    durationMs?: number;
}

/** Splits "24/7" into 24 and "/7" so only the leading number animates. */
const parse = (value: string) => {
    const match = value.match(/^(\D*)(\d[\d,]*)(.*)$/s);
    if (!match) return null;
    return {
        prefix: match[1],
        target: Number(match[2].replace(/,/g, '')),
        suffix: match[3],
        grouped: match[2].includes(','),
    };
};

/**
 * Counts a stat up from zero the first time it scrolls into view.
 * Non-numeric values, reduced-motion visitors and no-IO browsers just render
 * the final string.
 */
const CountUp: React.FC<CountUpProps> = ({ value, durationMs = 1600 }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const parts = parse(value);
    const [display, setDisplay] = useState(parts ? `${parts.prefix}0${parts.suffix}` : value);

    useEffect(() => {
        const node = ref.current;
        if (!node || !parts) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || typeof IntersectionObserver === 'undefined') {
            setDisplay(value);
            return;
        }

        let frame = 0;
        const format = (n: number) =>
            parts.grouped ? n.toLocaleString('en-IN') : String(n);

        const run = () => {
            const start = performance.now();
            const tick = (now: number) => {
                const t = Math.min((now - start) / durationMs, 1);
                // easeOutExpo — fast start, long settle, so the final digits
                // land readably rather than whipping past.
                const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
                setDisplay(`${parts.prefix}${format(Math.round(parts.target * eased))}${parts.suffix}`);
                if (t < 1) frame = requestAnimationFrame(tick);
            };
            frame = requestAnimationFrame(tick);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                observer.disconnect();
                run();
            },
            { threshold: 0.4 }
        );
        observer.observe(node);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
        };
        // `value` fully determines the parsed parts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, durationMs]);

    return (
        <span ref={ref} className="countup">
            {display}
        </span>
    );
};

export default CountUp;
